import React, { useState, useEffect, useRef, useContext, useMemo } from 'react'
import { Link, useLocation, useHistory } from 'react-router-dom'
import {FormattedDate, FormattedMessage, useIntl} from 'react-intl'
import Icon from 'components/Icon'
import { Flex as Loading } from 'components/Loading'
import { Button } from '@arnsbogroup/react-voicearchive'

import InvoiceRow from './components/InvoiceRow'
import InvoiceHeadRow from './components/InvoiceHeadRow'
import messages from './messages'

const meta = null

const CustomerBillingPage = (props) => {
  const tableRef = useRef(null)
  const location = useLocation()
  const history = useHistory()
  const { formatMessage } = useIntl()

  const params = new URLSearchParams(location.search.substr(1))
  const [loading, setLoading] = useState(true)
  const [allInvoices, setInvoices] = useState([])
  const [page, setPage] = useState(params.get('page') || 1)
  const [pageSize, setPageSize] = useState(params.get('pageSize') || 20)
  const [sortBy, setSortBy] = useState(params.get('sortBy') || 'dueDate')
  const [sortDirection, setSortDirection] = useState(params.get('sortDirection') || 'DESC')
  const [query, setQuery] = useState(params.get('query') || '')

  useEffect(() => {
    fetch(`/rest/v2/customer/invoices`, {
      credentials: 'same-origin',
      method: 'GET',
    })
      .then((response) => response.json())
      .then(({ success, meta, data, errors }) => {
        setLoading(false)

        if (success) {
          setInvoices(data)
        } else {
          window.alert(errors.join(' - '))
        }

        if (tableRef.current) {
          tableRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      })
      .catch((err) => {
        window.alert(err)
      })
  }, [])


  useEffect(() => {
    let params = new URLSearchParams()

    params.append('sortDirection', sortDirection)
    params.append('sortBy', sortBy)
    params.append('pageSize', 50)
    params.append('page', page)
    params.append('query', query)

    history.push(location.pathname + '?' + params.toString())
  }, [sortBy, sortDirection, page, query])

  const onSort = (key) => () => {
    if (sortBy !== key) {
      setSortBy(key)
    } else {
      setSortDirection(sortDirection === 'DESC' ? 'ASC' : 'DESC')
    }

    setPage(1)
  }

  const invoices = useMemo(() => {
    return allInvoices.filter((invoice) => {
      if (query.toString().length) {
        return invoice.invoiceId.toString().includes(query.toString()) ||
          invoice.orderId.toString().includes(query.toString())
      }

      return true
    }).sort((a, b) => {
      if (sortDirection === 'DESC') {
        return new Date(b[sortBy]) - new Date(a[sortBy])
      }

      return new Date(a[sortBy]) - new Date(b[sortBy])
    })
  }, [allInvoices, query, sortBy, sortDirection])

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-none flex justify-between items-center">
        <div className="grow px-3 xl:px-6 py-2 xl:py-4 flex items-center space-x-2 xl:space-x-4">
          <Icon icon="search" />

          <input
            defaultValue={query}
            onBlur={({ target: { value } }) => setQuery(value)}
            onChange={({ target: { value } }) => {
              if (value === '') {
                setQuery(value)
              }
            }}
            onKeyDown={({ key, target: { value } }) => {
              if (key === 'Enter' || value === '') {
                setQuery(value)
              }
            }}
            className="w-1/3 py-2 px-4 w-full text-sm"
            placeholder="Search by invoice # or order #.."
          />
        </div>
        <div className="flex items-center space-x-3 xl:space-x-6 pr-3">
          <a href="mailto:finance@voicearchive.com">
            <Button 
              variant="outline"
              icon={['fa', 'life-ring']}
            >
              <FormattedMessage {...messages.requestHelp}/>
            </Button>
          </a>
        </div>
      </div>

      <div className="grow flex w-full overflow-scroll" ref={tableRef}>
        <div className="h-full overflow-scroll flex flex-col w-full">
          <table className="overflow-scroll relative table-fixed w-full">
            <thead>
              <InvoiceHeadRow
                sortBy={sortBy}
                onSort={onSort}
                sortDirection={sortDirection}
              />
            </thead>
            <tbody>
              { invoices.slice(pageSize * (page -1), pageSize * page).map((invoice) => (
                <InvoiceRow
                  key={invoice.invoiceId}
                  invoice={invoice}
                />
              )) }
            </tbody>
          </table>

          { loading &&
            <Loading />
          }

          { !invoices.length && !loading &&
            <Loading>
              { query.length ? (
                <span><FormattedMessage{...messages.noResultsQuery}/>"{ query }"</span>
              ) : (
                <span><FormattedMessage{...messages.noResultsFilters}/></span>
              ) }
            </Loading>
          }
        </div>
      </div>

      { !!invoices.length && !loading &&
        <div className="w-full flex justify-between items-center px-3 xl:px-6 text-sm font-medium text-gray-600 uppercase tracking-wider group cursor-pointer hover:bg-gray-100 border-t border-gray-100">
          <div>{ (page * pageSize) - pageSize } – { page * pageSize } <FormattedMessage {...messages.of}/> { invoices.length }</div>

          <div className="grow py-3 xl:py-6 text-center text-white group-hover:text-gray-400" onClick={() => setPage((page) => parseInt(page) + 1)}>
            Load more
          </div>

          <div>
            { page > 1 &&
              <span className="px-6 cursor-pointer hover:text-primary" onClick={() => setPage((page) => parseInt(page, 10) - 1)}>
                <Icon icon="arrow-left" />
              </span>
            }

            <span>{ page } <FormattedMessage {...messages.of}/> { Math.floor(invoices.length / pageSize) }</span>

            { page < Math.floor(invoices.length / pageSize) &&
              <span className="pl-6 cursor-pointer hover:text-primary" onClick={() => setPage((page) => parseInt(page, 10) + 1)}>
                <Icon icon="arrow-right" />
              </span>
            }
          </div>
        </div>
      }
    </div>
  )
}

export default CustomerBillingPage
